import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import { supabase, getInvitationByToken, acceptInvitation } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import SEO from '../components/SEO'
import Logo from '../components/Logo'

const ROLE_LABELS = { admin: 'Admin', assistant: 'Assistant' }
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

export default function JoinPractice() {
  const [params]        = useSearchParams()
  const { user }        = useAuth()
  const navigate        = useNavigate()
  const toast           = useToast()
  const token           = params.get('token')

  const [invitation, setInvitation] = useState(null)
  const [loadingInv, setLoadingInv] = useState(true)
  const [accepting, setAccepting]   = useState(false)
  const [error, setError]           = useState(null)

  // Sign-up form state (shown if not logged in)
  const [password, setPassword]     = useState('')
  const [confirm, setConfirm]       = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [signingUp, setSigningUp]   = useState(false)
  // Cloudflare Turnstile token. Supabase requires this on auth.signUp
  // once CAPTCHA protection is enabled in the dashboard. The widget
  // populates it via onSuccess; we hold it in state to gate the submit
  // button. See SignUp.js for the same pattern.
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaRef = useRef(null)

  useEffect(() => {
    if (!token) { setError('No invitation token found in this link.'); setLoadingInv(false); return }
    getInvitationByToken(token).then(({ data, error }) => {
      if (error || !data) { setError('This invitation link is invalid or has expired.'); }
      else if (data.accepted) { setError('This invitation has already been used.') }
      else if (new Date(data.expires_at) < new Date()) { setError('This invitation has expired. Please ask the practice owner to send a new one.') }
      else setInvitation(data)
      setLoadingInv(false)
    })
  }, [token])

  const handleAccept = async () => {
    setAccepting(true)
    const { error } = await acceptInvitation(token)
    setAccepting(false)
    if (error) { toast(error.message, 'error') }
    else { toast('Welcome! You now have access to the practice.', 'success'); navigate('/dashboard') }
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    if (!invitation) return
    if (password.length < 8) { toast('Password must be at least 8 characters', 'error'); return }
    if (password !== confirm) { toast('Passwords do not match', 'error'); return }
    if (!acceptedTerms) { toast('You must accept the Terms & Conditions to create an account', 'error'); return }
    if (!captchaToken) { toast('Please wait a moment for the security check to complete', 'error'); return }
    setSigningUp(true)

    // Stash the invitation token so it survives the email-confirmation
    // roundtrip. useAuth picks it up the moment the user is authenticated
    // (either immediately after signUp if email confirmation is off, or
    // after the user clicks the confirmation link) and calls accept-invitation.
    //
    // The key MUST match the PENDING_INVITE_KEY constant in useAuth.js.
    // If you rename one, rename both.
    try { localStorage.setItem('rc_pending_invitation_token', token) } catch {}

    const { data, error } = await supabase.auth.signUp({
      email: invitation.invitee_email,
      password,
      options: {
        // Cloudflare Turnstile token. Supabase verifies it server-side
        // before processing the signup. Required since CAPTCHA was
        // enabled on this project; signups without it are rejected.
        captchaToken,
      },
    })

    if (error) {
      // Turnstile tokens are single-use. Reset the widget so the user
      // can retry without manually refreshing.
      captchaRef.current?.reset()
      setCaptchaToken('')
      setSigningUp(false)
      try { localStorage.removeItem('rc_pending_invitation_token') } catch {}

      // Handle the "already registered" case with a clear instruction
      // rather than attempting an automatic sign-in fallback. The user
      // signs in normally on /login, then clicks the invite link again
      // (which they can find in the original invitation email). This
      // path is rare enough that the extra step is acceptable, and it
      // avoids the brittleness of trying to do captcha-gated sign-in
      // inline on a page designed for signup.
      if (error.message.includes('already registered')) {
        toast('You already have an account. Please sign in first, then click the invitation link again.', 'error')
        return
      }
      toast(error.message, 'error')
      return
    }

    if (data.session) {
      // Email confirmation is off -- the user is already authenticated.
      // useAuth will consume the pending token on its next tick, but
      // call handleAccept too for an immediate UI transition.
      setSigningUp(false)
      handleAccept()
    } else {
      // Email confirmation is on -- the user must click a confirmation
      // link before they're authenticated. The token is in localStorage;
      // useAuth will consume it once they confirm and land on the app.
      setSigningUp(false)
      toast('Account created. Please check your email to confirm. Your invitation will be applied automatically once you sign in.', 'success')
    }
  }

  if (loadingInv) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <SEO title="Loading invitation" noindex />
      <div className="spinner" />
    </div>
  )

  if (error) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <SEO title="Invitation unavailable" noindex />
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>⚠️</div>
        <h2 style={{ marginBottom: 12 }}>Invitation unavailable</h2>
        <p style={{ color: 'var(--ink-muted)', marginBottom: 24 }}>{error}</p>
        <Link to="/login" className="btn btn-primary">Go to sign in</Link>
      </div>
    </div>
  )

  const isCorrectUser = user && user.email?.toLowerCase() === invitation.invitee_email.toLowerCase()
  const isWrongUser   = user && !isCorrectUser

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <SEO title="Join practice" noindex />
      <div style={{ position: 'fixed', top: 0, right: 0, width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(72,146,145,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="fade-up" style={{ width: '100%', maxWidth: 460 }}>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Logo variant="icon" size={48} style={{ margin: '0 auto 16px' }} />
          <h1 style={{ fontSize: '1.8rem', marginBottom: 8 }}>You have been invited</h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
            Join as <strong>{ROLE_LABELS[invitation.role]}</strong> on Receipt Chaser
          </p>
        </div>

        <div className="card" style={{ padding: 28, marginBottom: 20 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Invitation details</div>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
              <span style={{ color: 'var(--ink-muted)' }}>Email</span>
              <span style={{ fontWeight: 500 }}>{invitation.invitee_email}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
              <span style={{ color: 'var(--ink-muted)' }}>Role</span>
              <span style={{ fontWeight: 500 }}>{ROLE_LABELS[invitation.role]}</span>
            </div>
          </div>
        </div>

        {/* Wrong user logged in */}
        {isWrongUser && (
          <div className="card" style={{ padding: 24 }}>
            <p style={{ fontSize: '0.88rem', color: '#dc2626', marginBottom: 16 }}>
              You are signed in as <strong>{user.email}</strong> but this invitation was sent to <strong>{invitation.invitee_email}</strong>. Please sign out and sign in with the correct account.
            </p>
            <button className="btn btn-secondary" onClick={async () => { await supabase.auth.signOut(); window.location.reload() }}>
              Sign out
            </button>
          </div>
        )}

        {/* Correct user logged in */}
        {isCorrectUser && (
          <div className="card" style={{ padding: 24 }}>
            <p style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', marginBottom: 20 }}>
              You are signed in as <strong>{user.email}</strong>. Click below to accept the invitation and gain access to the practice.
            </p>
            <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleAccept} disabled={accepting}>
              {accepting ? 'Accepting...' : 'Accept invitation'}
            </button>
          </div>
        )}

        {/* Not logged in — show sign-up/sign-in options */}
        {!user && (
          <div className="card" style={{ padding: 28 }}>
            <p style={{ fontSize: '0.88rem', color: 'var(--ink-muted)', marginBottom: 20 }}>
              Create a password for your account at <strong>{invitation.invitee_email}</strong>, or sign in if you already have an account.
            </p>
            <form onSubmit={handleSignUp}>
              <div className="form-group">
                <label htmlFor="password">Create password</label>
                <input id="password" type="password" className="input" value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" minLength={8} required />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="confirm">Confirm password</label>
                <input id="confirm" type="password" className="input" value={confirm}
                  onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" required />
              </div>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                marginTop: 16, cursor: 'pointer',
                fontSize: '0.85rem', color: 'var(--ink-muted)', lineHeight: 1.5,
              }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={e => setAcceptedTerms(e.target.checked)}
                  style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 500 }}>
                    Terms &amp; Conditions
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 500 }}>
                    Privacy Policy
                  </Link>.
                </span>
              </label>
              {/* Cloudflare Turnstile -- see SignUp.js for the same
                  pattern. Required because Supabase rejects auth.signUp
                  calls without a valid captchaToken once CAPTCHA is
                  enabled at the project level. */}
              {TURNSTILE_SITE_KEY ? (
                <div style={{ marginTop: 16 }}>
                  <Turnstile
                    ref={captchaRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={token => setCaptchaToken(token)}
                    onError={() => setCaptchaToken('')}
                    onExpire={() => setCaptchaToken('')}
                    options={{ theme: 'light', size: 'flexible' }}
                  />
                </div>
              ) : null}
              <button type="submit" className="btn btn-primary btn-lg" disabled={signingUp || !acceptedTerms || !captchaToken}
                style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}>
                {signingUp ? 'Creating account...' : 'Create account and join'}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
              Already have an account?{' '}
              <Link to={`/login?redirect=/join?token=${token}`} style={{ color: 'var(--accent)', fontWeight: 500 }}>Sign in</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
